// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
//
// Commun — libellés transverses, utilisés par plusieurs domaines et ne relevant
// d'aucun en particulier : boutons, en-têtes de colonnes, filtres, états vides,
// confirmations, messages d'erreur et de succès.
//
// C'est le RESTE de la découpe, pas une catégorie choisie : ces clés sont celles
// qui n'appartenaient à aucun des neuf domaines métier. Une clé qui atterrit ici
// et qui, en réalité, ne sert qu'à un seul écran a sa place dans le module de cet
// écran — le vérifier par son usage réel, jamais par son préfixe. Deux exemples
// rencontrés pendant la découpe : `plus_*` n'est pas « PRO+ » mais le « plus »
// français, et `doc_validated` n'est pas un document projet.
//
// ⚠️ Lignes déplacées verbatim depuis `i18n.js`, sans réalignement : le catalogue
// figé (`tests/unit/test_i18n_parity.py`) compare le texte source caractère par
// caractère.

const commun = {
  fr: {
    validation_unavailable: 'Validation non disponible',
    delete:           'Supprimer',
    btn_refresh:           '🔄 Actualiser',
    export_excel:          '📥 Exporter Excel',
    no_data:               'Aucune donnée',
    filter_label:          'Filtrer',
    reset_label:           'Réinitialiser',
    col_type:        'Type',
    col_target:      'Cible',
    col_analysis:    'Analyse',
    col_user:        'Utilisateur',
    col_content:     'Contenu',
    col_date:        'Date',
    col_src:         'Src',
    col_actions:     'Actions',
    col_note:        'Note',
    col_comment:     'Commentaire',
    col_category:    'Catégorie',
    col_status:      'Statut',
    col_admin_reply: 'Réponse admin',
    table_sort_hint: 'Cliquer pour trier — un 2e clic inverse, un 3e annule le tri',
    filter_all:      'Tous',
    filter_date_from:'Date depuis',
    filter_date_to:  "Date jusqu'à",
    no_annotations:    'Aucune annotation trouvée',
    no_ratings:        'Aucune évaluation pour le moment.',
    no_suggestions:    'Aucune suggestion pour le moment.',
    no_ratings_filter: 'Aucune évaluation pour cette note',
    modified_label:          '✏️ modifié',
    save:                'Enregistrer',
    cancel:              'Annuler',
    search_placeholder:  'Rechercher par nom...',
    load_more:           'Charger plus',
    loading_more:        'Chargement...',
    btn_annotate:        '📝 Annoter',
    btn_rate:            '⭐ Évaluer',
    btn_delete_analysis: '🗑️ Supprimer',
    new_annotation:           'Nouvelle annotation',
    btn_add_annotation:       '➕ Ajouter',
    btn_save_annotation:      '✅ Sauvegarder',
    new_suggestion:           'Nouvelle suggestion',
    btn_submit_suggestion:    '➕ Soumettre',
    btn_sending:              '⏳ Envoi...',
    doc_validated:    'Document validé',
    doc_invalid:      'Document invalide',
    search_by_email:    'Rechercher par email...',
    tab_ratings:     '⭐ Évaluations',
    tab_suggestions: '💡 Suggestions',
    confirm_delete_analysis:  'Supprimer cette analyse ?',
    confirm_delete_upload:    'Supprimer ce fichier uploadé ?',
    confirm_cancel_analysis:  'Êtes-vous sûr de vouloir annuler cette analyse ?',
    alert_analysis_cancelled: '✅ Analyse annulée',
    alert_analysis_in_progress: '⚠️ Une analyse est en cours. Veuillez patienter.',
    alert_pdf_only:           '⚠️ Veuillez déposer un fichier PDF',
    alert_reload_page:        'Erreur de connexion. Rechargez la page.',
    err_prefix:               'Erreur: ',
    alert_interrupted:      '⚠️ L\'analyse précédente a été interrompue.\n\nVous pouvez relancer une nouvelle analyse.',
    alert_failed:           '⚠️ L\'analyse précédente a échoué.\n\nVous pouvez relancer une nouvelle analyse.',
    alert_too_large:        '❌ Fichier trop volumineux\n\nLimite : 50 MB\n\nVeuillez réduire la taille du PDF ou le découper.',
    alert_server_error:     '❌ Erreur serveur\n\nUne erreur technique est survenue. Veuillez réessayer ou contacter le support.',
    alert_existing_analysis:'ℹ️ Vous avez déjà une analyse en cours.\n\nElle a été restaurée.',
    alert_file_deleted:     '✅ Fichier supprimé',
    alert_copy_done:        '✅ Résumé copié dans le presse-papier !',
    alert_copy_short:       '✅ Résumé copié !',
    its_you:                'c\'est vous',
    loading_label:          'Chargement...',
    retry_label:            'Réessayer',
    create_analysis_cta:    'Créer une analyse',
    clear_search_label:     'Effacer la recherche',
    no_results_search:      'Aucun résultat pour',
    no_analyses:            'Aucune analyse pour le moment',
    btn_analyze:        '▶ Analyser',
    btn_invalid:        '🚫 Non valide',
    btn_too_large:      '🚫 Trop volumineux',
    btn_delete:         '🗑️ Supprimer',
    btn_new_analysis:   '🔄 Nouvelle analyse',
    btn_cancel:         '❌ Annuler',
    btn_copy_summary:   '📋 Copier le résumé',
    btn_dl_pdf:         '📄 Télécharger le rapport PDF',
    btn_dl_excel:       '📊 Télécharger l\'Excel',
    archived_label:     'Cette analyse est archivée. Lancez une nouvelle analyse depuis NORMA.',
    no_history:         'Aucun historique de conversation disponible pour cette analyse.',
    conformity:         'Appréciation',
    back_to_analyses: '← Retour à mes analyses',
    confirm_action: 'Confirmer',
    err_name_empty:     'Le nom ne peut pas être vide',
    err_no_change:      'Aucun changement',
    err_email_required: 'Email requis',
    err_pw_required:    'Mot de passe requis',
    err_fields_required:'Tous les champs sont requis',
    err_pw_mismatch:    'Les mots de passe ne correspondent pas',
    err_pw_min:         '8 caractères minimum',
    ok_name_updated:    '✅ Nom mis à jour',
    ok_email_updated:   '✅ Email mis à jour',
    ok_pw_updated:      '✅ Mot de passe modifié',
    saving:             '⏳...',
    changing_pw:        '⏳ Modification...',
    dashboard_project_not_validated: 'Le projet sélectionné est en préparation et n\'a pas encore été validé. Veuillez valider le projet depuis la page Projets avant de lancer une analyse.',
    favorable_label:      'FAVORABLE(S)',
    defavorable_label:    'DÉFAVORABLE(S)',
    avec_reserve_label:   'AVEC RÉSERVE(S)',
    filter_status_all:        'Tous les statuts',
    filter_result_all:        'Tous les résultats',
    filter_project_all:       'Tous les projets',
    filter_user_ph:           'Email utilisateur...',
    // Vue équipe de « Mes analyses » (15/09/2026) — administrateur de licence.
    vue_mes_analyses:         'Mes analyses',
    vue_toute_equipe:         "Toute l'équipe",
    analyse_lecture_seule:    'Analyse de {auteur} — consultation seule',
    // Trois positions, trois mots (doctrine du 13/08/2026) : jamais
    // « favorable avec réserve », jamais « partiellement favorable ».
    filter_result_conforme:   'Favorable',
    filter_result_reserve:    'Réserve',
    filter_result_nc:         'Défavorable',
    avis_favorable:           'Favorable',
    avis_reserve:             'Réserve',
    avis_defavorable:         'Défavorable',
    avis_non_evalue:          'Non évalué',
    filter_date_from_label:   'Depuis',
    filter_date_to_label:     "Jusqu'au",
    filter_active:            'Filtres actifs',
    disclaimer_title: "Outil d'aide à la décision",
    disclaimer_body:  "Ce résultat est fourni à titre indicatif par Normascan. Il ne constitue pas un avis professionnel certifié et ne saurait remplacer l'analyse d'un expert qualifié. La décision finale et toute responsabilité demeurent celles du professionnel utilisateur.",
    field_email:                'Email',
    err_generic:                'Erreur',
    err_network:                'Erreur réseau',
  },

  en: {
    validation_unavailable: 'Validation unavailable',
    delete:           'Delete',
    btn_refresh:           '🔄 Refresh',
    export_excel:          '📥 Export Excel',
    no_data:               'No data',
    filter_label:          'Filter',
    reset_label:           'Reset',
    col_type:        'Type',
    col_target:      'Target',
    col_analysis:    'Analysis',
    col_user:        'User',
    col_content:     'Content',
    col_date:        'Date',
    col_src:         'Src',
    col_actions:     'Actions',
    col_note:        'Rating',
    col_comment:     'Comment',
    col_category:    'Category',
    col_status:      'Status',
    col_admin_reply: 'Admin reply',
    table_sort_hint: 'Click to sort — click again to reverse, a third time to clear',
    filter_all:      'All',
    filter_date_from:'From date',
    filter_date_to:  'To date',
    no_annotations:    'No annotations found',
    no_ratings:        'No ratings yet.',
    no_suggestions:    'No suggestions yet.',
    no_ratings_filter: 'No ratings for this score',
    modified_label:          '✏️ edited',
    save:                'Save',
    cancel:              'Cancel',
    search_placeholder:  'Search by name...',
    load_more:           'Load more',
    loading_more:        'Loading...',
    btn_annotate:        '📝 Annotate',
    btn_rate:            '⭐ Rate',
    btn_delete_analysis: '🗑️ Delete',
    new_annotation:           'New annotation',
    btn_add_annotation:       '➕ Add',
    btn_save_annotation:      '✅ Save',
    new_suggestion:           'New suggestion',
    btn_submit_suggestion:    '➕ Submit',
    btn_sending:              '⏳ Sending...',
    doc_validated:    'Document validated',
    doc_invalid:      'Document invalid',
    search_by_email: 'Search by email...',
    tab_ratings:     '⭐ Ratings',
    tab_suggestions: '💡 Suggestions',
    confirm_delete_analysis:  'Delete this analysis?',
    confirm_delete_upload:    'Delete this uploaded file?',
    confirm_cancel_analysis:  'Are you sure you want to cancel this analysis?',
    alert_analysis_cancelled: '✅ Analysis cancelled',
    alert_analysis_in_progress: '⚠️ An analysis is in progress. Please wait.',
    alert_pdf_only:           '⚠️ Please drop a PDF file',
    alert_reload_page:        'Connection error. Please reload the page.',
    err_prefix:               'Error: ',
    alert_interrupted:      '⚠️ The previous analysis was interrupted.\n\nYou can start a new analysis.',
    alert_failed:           '⚠️ The previous analysis failed.\n\nYou can start a new analysis.',
    alert_too_large:        '❌ File too large\n\nLimit: 50 MB\n\nPlease reduce the PDF size or split it.',
    alert_server_error:     '❌ Server error\n\nA technical error occurred. Please try again or contact support.',
    alert_existing_analysis:'ℹ️ You already have an analysis in progress.\n\nIt has been restored.',
    alert_file_deleted:     '✅ File deleted',
    alert_copy_done:        '✅ Summary copied to clipboard!',
    alert_copy_short:       '✅ Summary copied!',
    its_you:                'that\'s you',
    create_analysis_cta:    'Create an analysis',
    clear_search_label:     'Clear search',
    no_results_search:      'No results for',
    no_analyses:            'No analyses yet',
    loading_label:          'Loading...',
    retry_label:            'Retry',
    btn_analyze:        '▶ Analyse',
    btn_invalid:        '🚫 Invalid',
    btn_too_large:      '🚫 Too large',
    btn_delete:         '🗑️ Remove',
    btn_new_analysis:   '🔄 New analysis',
    btn_cancel:         '❌ Cancel',
    btn_copy_summary:   '📋 Copy summary',
    btn_dl_pdf:         '📄 Download PDF report',
    btn_dl_excel:       '📊 Download Excel',
    archived_label:     'This analysis is archived. Start a new analysis from NORMA.',
    no_history:         'No conversation history available for this analysis.',
    conformity:         'Assessment',
    back_to_analyses:   '← Back to my analyses',
    confirm_action: 'Confirm',
    err_name_empty:     'Name cannot be empty',
    err_no_change:      'No changes',
    err_email_required: 'Email required',
    err_pw_required:    'Password required',
    err_fields_required:'All fields are required',
    err_pw_mismatch:    'Passwords do not match',
    err_pw_min:         'Minimum 8 characters',
    ok_name_updated:    '✅ Name updated',
    ok_email_updated:   '✅ Email updated',
    ok_pw_updated:      '✅ Password changed',
    saving:             '⏳...',
    changing_pw:        '⏳ Updating...',
    dashboard_project_not_validated: 'The selected project is in draft status and has not been validated yet. Please validate the project from the Projects page before running an analysis.',
    favorable_label:      'FAVORABLE(S)',
    defavorable_label:    'UNFAVORABLE(S)',
    avec_reserve_label:   'WITH RESERVATION(S)',
    filter_status_all:        'All statuses',
    filter_result_all:        'All results',
    filter_project_all:       'All projects',
    filter_user_ph:           'User email...',
    vue_mes_analyses:         'My analyses',
    vue_toute_equipe:         'Whole team',
    analyse_lecture_seule:    'Analysis by {auteur} — view only',
    // Three positions, three words (13/08/2026 doctrine): never
    // « favorable with reservation », never « partially favorable ».
    filter_result_conforme:   'Favorable',
    filter_result_reserve:    'Reservation',
    filter_result_nc:         'Unfavorable',
    avis_favorable:           'Favorable',
    avis_reserve:             'Reservation',
    avis_defavorable:         'Unfavorable',
    avis_non_evalue:          'Not assessed',
    filter_date_from_label:   'From',
    filter_date_to_label:     'Until',
    filter_active:            'Active filters',
    disclaimer_title: 'Decision support tool',
    disclaimer_body:  'This result is provided for informational purposes by Normascan. It does not constitute a certified professional opinion and cannot replace the analysis of a qualified expert. The final decision and all responsibility remain with the professional user.',
    field_email:                'Email',
    err_generic:                'Error',
    err_network:                'Network error',
  },
};

export default commun;
